import { AnimationEvent } from '@angular/animations';
import { NgClass, NgComponentOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  InjectionToken,
  Injector,
  input,
  OnDestroy,
  Renderer2,
  signal,
  Type,
  viewChild,
  DOCUMENT
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { ReplaySubject, filter, fromEvent, map, takeUntil } from 'rxjs';

import { ScreenService } from '@core/services/screen.service';

import animations from './animations';
import { DrawerService } from './drawer.service';

export type DrawerStatus = 'open' | 'closed';

export type DrawerPosition = 'left' | 'right';
export type DrawerSize = 'sm' | 'md' | 'lg';

type DrawerComponentTheme = {
  type: 'colorized';
  variant: 'primary' | 'secondary' | 'tertiary';
};

export interface DrawerComponentOptions {
  backdropClose?: boolean;
  position?: DrawerPosition;
  size?: DrawerSize;
  theme?: 'default' | DrawerComponentTheme;
}

interface DrawerComponentData<T> {
  component: Type<T>;
  title?: string;
  description?: string;
  options?: DrawerComponentOptions;
}

interface DrawerServiceResource {
  service: DrawerService;
  removeComponentFromBody(): void;
}

export type DrawerData<T> = {
  [P in keyof T]: T[P];
};

export const DRAWER_DATA = new InjectionToken<DrawerComponentData<unknown>>(
  'DRAWER_DATA'
);

const DEFAULT_OPTIONS: DrawerComponentOptions = {
  backdropClose: true,
  position: 'right',
  size: 'md',
  theme: 'default'
};

@Component({
  selector: 'ui-drawer',
  standalone: true,
  imports: [NgClass, NgComponentOutlet],
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  animations
})
export class DrawerComponent<T = unknown, R = unknown>
  implements AfterViewInit, OnDestroy
{
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly renderer = inject(Renderer2);
  private readonly screenService = inject(ScreenService);

  readonly title = input('');
  readonly description = input('');
  readonly component = input<Type<T> | null>(null);
  readonly drawerIndex = input(0);
  readonly options = input<DrawerComponentOptions, DrawerComponentOptions>(
    DEFAULT_OPTIONS,
    {
      transform: (value: Partial<DrawerComponentOptions>) => ({
        ...DEFAULT_OPTIONS,
        ...value
      })
    }
  );

  private _resource: DrawerServiceResource | undefined;

  get resource(): DrawerServiceResource | undefined {
    return this._resource;
  }

  set resource(value: DrawerServiceResource | undefined) {
    this._resource = value;
  }

  protected id = `drawer-${Math.random().toString(36).substring(2, 9)}`;

  protected dynamicComponentInjector: Injector | undefined;

  private result: R;
  private _closed = new ReplaySubject<R>(1);

  readonly isOpen = signal(false);
  readonly isOpen$ = toObservable(this.isOpen);

  get closed$() {
    return this._closed.asObservable().pipe(
      map((data) => ({
        data: data ?? ({} as R),
        index: this.drawerIndex()
      }))
    );
  }

  get status$() {
    return this.isOpen$.pipe(
      map((isOpen) => (isOpen ? 'open' : 'closed') as DrawerStatus)
    );
  }

  readonly position = computed(() => this.options().position);

  readonly size = computed(() => this.options().size);

  readonly theme = computed(() => {
    const theme = this.options().theme;

    if (theme && typeof theme === 'object') {
      return theme.variant;
    }

    return 'default';
  });

  readonly bgColor = computed(() => {
    const theme = this.theme();

    return {
      'bg-white': theme === 'default',
      'bg-brand-primary-lightest': theme === 'primary',
      'bg-brand-secondary-lightest': theme === 'secondary',
      'bg-brand-tertiary-lightest': theme === 'tertiary'
    };
  });

  readonly closeButtonColor = computed(() => {
    const theme = this.theme();

    return {
      'text-neutral-low-light hover:text-neutral-low-medium focus:ring-brand-primary-pure':
        theme === 'default',
      'text-brand-primary-light hover:text-brand-primary-pure focus:ring-brand-primary-pure':
        theme === 'primary',
      'text-brand-secondary-pure hover:text-brand-secondary-dark focus:ring-brand-secondary-pure':
        theme === 'secondary',
      'text-brand-tertiary-pure hover:text-brand-tertiary-dark focus:ring-brand-tertiary-pure':
        theme === 'tertiary'
    };
  });

  protected readonly drawerContent =
    viewChild<ElementRef<HTMLDivElement>>('drawerContent');
  protected readonly drawerBackdrop =
    viewChild<ElementRef<HTMLDivElement>>('drawerBackdrop');

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.isOpen()) {
      this.handleClose();
    }
  }

  ngOnDestroy(): void {
    this.dynamicComponentInjector = undefined;

    if (this.resource && this.id) {
      this._closed.next(this.result);
      this._closed.complete();
    }
  }

  ngAfterViewInit(): void {
    this.adjustSize('open');

    fromEvent(this.drawerBackdrop().nativeElement, 'click')
      .pipe(
        takeUntil(this._closed.asObservable().pipe(map((is) => !!is))),
        filter(() => this.isOpen() && this.options().backdropClose),
        filter((event: Event) => {
          return event.target === this.drawerBackdrop().nativeElement;
        })
      )
      .subscribe(() => {
        this.handleClose();
      });
  }

  onTransitionEnd(event: AnimationEvent): void {
    if (event.toState.startsWith('closed')) {
      this.handleBodyScroll();

      if (!this.isOpen()) {
        if (this.resource && this.id) {
          this.resource.removeComponentFromBody();
          this._closed.next(this.result);
        }
      }
    }
  }

  onTransitionStart(event: AnimationEvent): void {
    if (event.toState.startsWith('open')) {
      this.handleBodyScroll();
    }
  }

  open(id: string, props: T): void {
    this.id = id;
    this.isOpen.set(true);

    this.dynamicComponentInjector = Injector.create({
      providers: [
        {
          provide: DRAWER_DATA,
          useFactory: () => props,
          deps: []
        }
      ],
      name: 'DrawerComponentInjector',
      parent: this.injector
    });
  }

  handleClose(): void {
    this.resource.service.close();
  }

  close(result?: R): void {
    this.adjustSize('closed');

    this.isOpen.set(false);

    if (result) {
      this.result = result;
    }
  }

  private adjustSize(on?: 'open' | 'closed'): void {
    if (this.drawerIndex() === 0) {
      return;
    }

    const drawerWidth = this.drawerContent().nativeElement.offsetWidth;
    const overlap = drawerWidth / 2;
    const isLeft = this.position() === 'left';

    if (on === 'open') {
      this.adjustDrawersOnOpen(overlap, isLeft);
    } else if (on === 'closed') {
      this.adjustDrawersOnClose(overlap, isLeft);
    }
  }

  private adjustDrawersOnOpen(overlap: number, isLeft: boolean): void {
    for (let i = 0; i < this.drawerIndex(); i++) {
      const drawerContainer = this.document.querySelector(
        `[data-drawer-index="${i}"]`
      ) as HTMLElement;

      if (this.isDrawerOnSide(drawerContainer, isLeft)) {
        const translateX = (this.drawerIndex() - i) * overlap;
        this.renderer.setStyle(
          drawerContainer,
          'transform',
          `translateX(${isLeft ? translateX : -translateX}px)`
        );
      }
    }
  }

  private adjustDrawersOnClose(overlap: number, isLeft: boolean): void {
    for (let i = 0; i < this.drawerIndex() - 1; i++) {
      const drawerContainer = this.document.querySelector(
        `[data-drawer-index="${i}"]`
      ) as HTMLElement;

      if (this.isDrawerOnSide(drawerContainer, isLeft)) {
        const translateX = (this.drawerIndex() - i - 1) * overlap;
        this.renderer.setStyle(
          drawerContainer,
          'transform',
          `translateX(${isLeft ? translateX : -translateX}px)`
        );
      }
    }

    this.resetFirstDrawerPosition();
  }

  private isDrawerOnSide(
    drawerContainer: HTMLElement,
    isLeft: boolean
  ): boolean {
    const drawerPosition = drawerContainer?.getAttribute(
      'data-drawer-position'
    );
    return drawerPosition === (isLeft ? 'left' : 'right');
  }

  private resetFirstDrawerPosition(): void {
    const firstDrawerContainer = this.document.querySelector(
      `[data-drawer-index="${this.drawerIndex() - 1}"]`
    ) as HTMLElement;

    this.renderer.setStyle(
      firstDrawerContainer,
      'transform',
      `translateX(0px)`
    );
  }

  private handleBodyScroll(): void {
    const MOBILE_BREAKPOINT = 640;
    const body = document.body;
    const isMobile = this.screenService.isMobileDevice(MOBILE_BREAKPOINT);

    if (this.isOpen()) {
      if (!isMobile) {
        const hasScrollbar =
          window.innerWidth > document.documentElement.clientWidth;
        if (hasScrollbar) {
          const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;
          body.style.paddingRight = `${scrollbarWidth}px`;
        }
      }
      body.classList.add('overflow-hidden');
    } else {
      body.classList.remove('overflow-hidden');
      if (!isMobile) {
        body.style.paddingRight = '';
      }
    }
  }
}
